import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plug, 
  Settings, 
  Activity, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Plus, 
  Search,
  Globe,
  Database,
  Webhook,
  Key
} from 'lucide-react';

interface Integration {
  id: string;
  name: string;
  type: 'api' | 'webhook' | 'database' | 'file';
  status: 'active' | 'inactive' | 'error' | 'pending';
  provider: string;
  lastSync: string;
  description: string;
  endpoints: number;
  requests: number;
}

export function AdvancedIntegrationHub() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);

  const integrations: Integration[] = [
    {
      id: '1',
      name: 'Utility Data API',
      type: 'api',
      status: 'active',
      provider: 'EnergyCorp',
      lastSync: '2 minutes ago',
      description: 'Real-time utility bill data integration',
      endpoints: 8,
      requests: 15420
    },
    {
      id: '2',
      name: 'Payment Gateway',
      type: 'webhook',
      status: 'active',
      provider: 'PaymentPro',
      lastSync: '5 minutes ago',
      description: 'Payment processing and notifications',
      endpoints: 3,
      requests: 8950
    },
    {
      id: '3',
      name: 'Customer Database',
      type: 'database',
      status: 'inactive',
      provider: 'DataVault',
      lastSync: '2 hours ago',
      description: 'Customer information synchronization',
      endpoints: 12,
      requests: 25680
    },
    {
      id: '4',
      name: 'Document Storage',
      type: 'file',
      status: 'error',
      provider: 'CloudFiles',
      lastSync: '1 day ago',
      description: 'PDF and document file management',
      endpoints: 5,
      requests: 3240
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600';
      case 'inactive': return 'text-gray-600';
      case 'error': return 'text-red-600';
      case 'pending': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4" />;
      case 'inactive': return <Clock className="h-4 w-4" />;
      case 'error': return <AlertCircle className="h-4 w-4" />;
      case 'pending': return <Activity className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'api': return <Globe className="h-4 w-4" />;
      case 'webhook': return <Webhook className="h-4 w-4" />;
      case 'database': return <Database className="h-4 w-4" />;
      case 'file': return <Key className="h-4 w-4" />;
      default: return <Plug className="h-4 w-4" />;
    }
  };

  const filteredIntegrations = integrations.filter(integration => {
    const matchesSearch = integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         integration.provider.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || integration.type === filterType;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Advanced Integration Hub</h1>
          <p className="text-muted-foreground">Manage all your third-party integrations and APIs</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Integration
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Integration</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Integration Name</Label>
                <Input id="name" placeholder="Enter integration name" />
              </div>
              <div>
                <Label htmlFor="type">Type</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select integration type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="api">REST API</SelectItem>
                    <SelectItem value="webhook">Webhook</SelectItem>
                    <SelectItem value="database">Database</SelectItem>
                    <SelectItem value="file">File Storage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="endpoint">Endpoint URL</Label>
                <Input id="endpoint" placeholder="https://api.example.com" />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" placeholder="Describe the integration purpose" />
              </div>
              <Button className="w-full">Create Integration</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search integrations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="api">API</SelectItem>
            <SelectItem value="webhook">Webhook</SelectItem>
            <SelectItem value="database">Database</SelectItem>
            <SelectItem value="file">File Storage</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Integration Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredIntegrations.map((integration) => (
          <Card key={integration.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getTypeIcon(integration.type)}
                  <CardTitle className="text-lg">{integration.name}</CardTitle>
                </div>
                <div className={`flex items-center gap-1 ${getStatusColor(integration.status)}`}>
                  {getStatusIcon(integration.status)}
                  <Badge variant="outline" className={getStatusColor(integration.status)}>
                    {integration.status}
                  </Badge>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">{integration.description}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-medium">Provider</div>
                  <div className="text-muted-foreground">{integration.provider}</div>
                </div>
                <div>
                  <div className="font-medium">Last Sync</div>
                  <div className="text-muted-foreground">{integration.lastSync}</div>
                </div>
                <div>
                  <div className="font-medium">Endpoints</div>
                  <div className="text-muted-foreground">{integration.endpoints}</div>
                </div>
                <div>
                  <div className="font-medium">Requests</div>
                  <div className="text-muted-foreground">{integration.requests.toLocaleString()}</div>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setSelectedIntegration(integration)}
                >
                  <Settings className="h-4 w-4 mr-1" />
                  Configure
                </Button>
                <Button variant="outline" size="sm">
                  <Activity className="h-4 w-4 mr-1" />
                  Test
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Integration Details Dialog */}
      {selectedIntegration && (
        <Dialog open={!!selectedIntegration} onOpenChange={() => setSelectedIntegration(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Configure {selectedIntegration.name}</DialogTitle>
            </DialogHeader>
            <Tabs defaultValue="settings" className="space-y-4">
              <TabsList>
                <TabsTrigger value="settings">Settings</TabsTrigger>
                <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
                <TabsTrigger value="logs">Activity Logs</TabsTrigger>
              </TabsList>
              
              <TabsContent value="settings" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="provider">Provider</Label>
                    <Input id="provider" value={selectedIntegration.provider} />
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select value={selectedIntegration.status}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" value={selectedIntegration.description} />
                </div>
                <Button>Save Changes</Button>
              </TabsContent>
              
              <TabsContent value="endpoints" className="space-y-4">
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div>
                        <div className="font-medium">GET /api/bills</div>
                        <div className="text-sm text-muted-foreground">Retrieve bill data</div>
                      </div>
                      <Badge variant="secondary">Active</Badge>
                    </div>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="logs" className="space-y-4">
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div>
                        <div className="font-medium">API Request Successful</div>
                        <div className="text-sm text-muted-foreground">{i} minutes ago</div>
                      </div>
                      <Badge variant="outline" className="text-green-600">200</Badge>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}